/**
 * Main navigation header component
 * Displays logo, navigation links, and user info
 * Mobile-responsive with hamburger menu
 *
 * @module Header
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { useAuth, useIsAdmin } from '../../hooks/useAuth'
import { ROUTES } from '../../utils/constants'
import walrusLogo from '/Walrus_transparent.png'

/**
 * Header component props
 */
interface HeaderProps {
  /**
   * Optional CSS class name for additional styling
   */
  className?: string
}

/**
 * Main navigation header component
 * Shows logo, navigation links, and user authentication info
 * Responsive hamburger menu on mobile devices
 */
export function Header({ className = '' }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const auth = useAuth()
  const isAdmin = useIsAdmin()
  const navigate = useNavigate()

  logger.debug('[WALRUS] Header: rendering', {
    isAuthenticated: auth.isAuthenticated,
    userEmail: auth.user?.email,
    isAdmin,
    mobileMenuOpen,
  })

  /**
   * Handle logout action
   */
  const handleLogout = async () => {
    logger.debug('[WALRUS] Header.handleLogout: initiating logout')
    try {
      await auth.signOut()
      logger.info('[WALRUS] Header.handleLogout: logout successful')
      navigate(ROUTES.HOME, { replace: true })
    } catch (error) {
      logger.error('[WALRUS] Header.handleLogout: logout failed', error)
    }
  }

  /**
   * Close mobile menu when a link is clicked
   */
  const handleNavClick = () => {
    logger.debug('[WALRUS] Header.handleNavClick: closing mobile menu')
    setMobileMenuOpen(false)
  }

  return (
    <header
      className={`bg-walrus-white border-b border-walrus-gray sticky top-0 z-50 ${className}`}
      role="banner"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Site Title */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            onClick={() => logger.debug('[WALRUS] Header: navigating to home')}
          >
            <img
              src={walrusLogo}
              alt="Rodrigo the Walrus"
              className="h-10 w-10 object-contain"
            />
            <span className="hidden sm:inline text-walrus-black font-display font-bold text-lg tracking-wider">
              RODRIGO
            </span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {/* Home Link */}
            <Link
              to={ROUTES.HOME}
              className="px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
              onClick={() => logger.debug('[WALRUS] Header: navigating to home')}
            >
              Home
            </Link>

            {/* Protected Routes - Gallery and Profile */}
            {auth.isAuthenticated && (
              <>
                <Link
                  to={ROUTES.GALLERY}
                  className="px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
                  onClick={() => logger.debug('[WALRUS] Header: navigating to gallery')}
                >
                  Gallery
                </Link>

                <Link
                  to={ROUTES.PROFILE}
                  className="px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
                  onClick={() => logger.debug('[WALRUS] Header: navigating to profile')}
                >
                  Profile
                </Link>
              </>
            )}

            {/* Admin Route - Admin Only */}
            {isAdmin && (
              <Link
                to={ROUTES.ADMIN}
                className="px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
                onClick={() => logger.debug('[WALRUS] Header: navigating to admin')}
              >
                Admin
              </Link>
            )}
          </div>

          {/* User Info and Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {auth.isAuthenticated ? (
              <>
                <div className="flex flex-col items-end">
                  <p className="text-walrus-black text-sm font-medium">
                    {auth.user?.displayName || auth.user?.email}
                  </p>
                  {auth.user?.displayName && (
                    <p className="text-walrus-silver text-xs">{auth.user?.email}</p>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-walrus-black text-walrus-white rounded-md hover:bg-walrus-charcoal transition-colors text-sm font-medium"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to={ROUTES.LOGIN}
                className="px-4 py-2 bg-walrus-black text-walrus-white rounded-md hover:bg-walrus-charcoal transition-colors text-sm font-medium"
                onClick={() => logger.debug('[WALRUS] Header: navigating to login')}
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => {
              logger.debug('[WALRUS] Header: toggling mobile menu')
              setMobileMenuOpen(!mobileMenuOpen)
            }}
            className="md:hidden p-2 rounded-md text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream focus:outline-none focus:ring-2 focus:ring-walrus-black"
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? (
              // Close icon (X)
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              // Hamburger icon
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <Link
              to={ROUTES.HOME}
              className="block px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
              onClick={handleNavClick}
            >
              Home
            </Link>

            {auth.isAuthenticated && (
              <>
                <Link
                  to={ROUTES.GALLERY}
                  className="block px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
                  onClick={handleNavClick}
                >
                  Gallery
                </Link>

                <Link
                  to={ROUTES.PROFILE}
                  className="block px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
                  onClick={handleNavClick}
                >
                  Profile
                </Link>
              </>
            )}

            {isAdmin && (
              <Link
                to={ROUTES.ADMIN}
                className="block px-3 py-2 text-walrus-silver hover:text-walrus-black hover:bg-walrus-cream rounded-md transition-colors text-sm font-medium"
                onClick={handleNavClick}
              >
                Admin
              </Link>
            )}

            <div className="border-t border-walrus-gray pt-2 mt-2">
              {auth.isAuthenticated ? (
                <>
                  <div className="px-3 py-2 mb-2">
                    <p className="text-walrus-black text-sm font-medium">
                      {auth.user?.displayName || auth.user?.email}
                    </p>
                    {auth.user?.displayName && (
                      <p className="text-walrus-silver text-xs">{auth.user?.email}</p>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 bg-walrus-black text-walrus-white rounded-md hover:bg-walrus-charcoal transition-colors text-sm font-medium"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to={ROUTES.LOGIN}
                  className="block px-3 py-2 bg-walrus-black text-walrus-white rounded-md hover:bg-walrus-charcoal transition-colors text-sm font-medium text-center"
                  onClick={handleNavClick}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
